import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Play, Pause, Shield, FileText, Archive, Lock, Download,
  CheckCircle, Clock, Users, ArrowRight, Zap, ChevronLeft, ChevronRight,
  Database, Key, Mail, BarChart3, Sparkles, Eye, RefreshCw,
  Building2, Calculator, FileCheck, Send
} from 'lucide-react';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: "easeOut" } }
};

const slideInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.4 } }
};

const slideInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.4 } }
};

// ============================================
// SLIDE COMPONENTS - Onboarding Rapide
// ============================================

const SlideIntro = () => (
  <motion.div 
    className="text-center py-8"
    initial="hidden"
    animate="visible"
    variants={staggerContainer}
  >
    <motion.div 
      variants={scaleIn}
      className="w-28 h-28 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-400 flex items-center justify-center shadow-2xl shadow-blue-500/30"
    >
      <motion.div
        animate={{ rotate: [0, 10, -10, 0] }}
        transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
      >
        <Zap className="w-14 h-14 text-white" />
      </motion.div>
    </motion.div>
    
    <motion.h3 variants={fadeInUp} className="text-3xl font-bold text-slate-900 mb-4">
      Bienvenue sur SmartFacture AI
    </motion.h3>
    
    <motion.p variants={fadeInUp} className="text-slate-600 max-w-lg mx-auto text-lg">
      Votre solution de facturation intelligente, conforme et sécurisée 
      pour les professionnels français.
    </motion.p>
    
    <motion.div 
      variants={staggerContainer}
      className="flex flex-wrap justify-center gap-4 mt-10"
    >
      {[
        { icon: Shield, label: "Conforme 2026", color: "emerald" },
        { icon: Lock, label: "Sécurisé", color: "blue" },
        { icon: Sparkles, label: "Simple", color: "amber" },
      ].map((item, i) => (
        <motion.div 
          key={i}
          variants={scaleIn}
          whileHover={{ scale: 1.05 }}
          className={`flex items-center gap-2 px-4 py-2 rounded-full bg-${item.color}-50 border border-${item.color}-200`}
        >
          <item.icon className={`w-5 h-5 text-${item.color}-600`} />
          <span className={`font-medium text-${item.color}-700`}>{item.label}</span>
        </motion.div>
      ))}
    </motion.div>
  </motion.div>
);

const SlideCreation = () => {
  const [step, setStep] = useState(0);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setStep(prev => (prev + 1) % 5);
    }, 1500);
    return () => clearInterval(timer);
  }, []);

  return (
    <motion.div 
      className="py-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <div className="bg-slate-100 rounded-2xl p-6 max-w-lg mx-auto shadow-inner">
        <motion.div variants={fadeInUp} className="bg-white rounded-xl shadow-lg p-5 border border-slate-200">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <span className="font-bold text-slate-900">Nouvelle Facture</span>
            </div>
            <motion.span 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium"
            >
              INV-00001
            </motion.span>
          </div>
          
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: step >= 0 ? 1 : 0.3, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <label className="text-xs text-slate-500 font-medium">Client</label>
              <div className={`bg-slate-50 rounded-lg p-3 text-sm font-medium border-2 transition-colors ${step === 0 ? 'border-blue-400' : 'border-transparent'}`}>
                {step >= 0 ? (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    Cabinet Martin & Associés
                  </motion.span>
                ) : '...'}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: step >= 1 ? 1 : 0.3, x: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <label className="text-xs text-slate-500 font-medium">Prestation</label>
              <div className={`bg-slate-50 rounded-lg p-3 text-sm border-2 transition-colors ${step === 1 ? 'border-blue-400' : 'border-transparent'}`}>
                <div className="flex justify-between">
                  <span>Prestation comptable mensuelle</span>
                  <span className="font-bold text-slate-900">850,00 € HT</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: step >= 2 ? 1 : 0 }}
              className="border-t border-slate-200 pt-4"
            >
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-500">TVA 20%</span>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: step >= 2 ? 1 : 0 }}
                  className="font-medium"
                >
                  170,00 €
                </motion.span>
              </div>
              <div className="flex justify-between text-lg font-bold">
                <span>Total TTC</span>
                <motion.span 
                  className="text-blue-600"
                  animate={{ scale: step === 3 ? [1, 1.1, 1] : 1 }}
                >
                  1 020,00 €
                </motion.span>
              </div>
            </motion.div>
          </div>
        </motion.div>
        
        <motion.div 
          className="flex justify-center mt-5"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: step >= 4 ? 1 : 0.5, y: 0 }}
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`bg-gradient-to-r from-blue-600 to-blue-500 text-white px-8 py-3 rounded-xl flex items-center gap-2 font-semibold shadow-lg shadow-blue-500/30 ${step === 4 ? 'ring-4 ring-blue-300 ring-opacity-50' : ''}`}
          >
            <FileText className="w-5 h-5" />
            Générer PDF
          </motion.button>
        </motion.div>
      </div>
      
      <motion.div 
        variants={fadeInUp}
        className="flex items-center justify-center gap-2 mt-6 text-slate-600"
      >
        <Clock className="w-5 h-5 text-emerald-500" />
        <span className="font-medium">Création en moins de 30 secondes</span>
      </motion.div>
    </motion.div>
  );
};

const SlideArchivage = () => {
  const files = [
    { name: "INV-00001.pdf", date: "22/02/2026" },
    { name: "INV-00002.pdf", date: "18/02/2026" },
    { name: "INV-00003.pdf", date: "15/02/2026" },
  ];

  return (
    <motion.div 
      className="py-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <div className="max-w-lg mx-auto">
        <motion.div 
          variants={scaleIn}
          className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 text-white overflow-hidden"
        >
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '24px 24px'
            }}/>
          </div>
          
          <div className="relative z-10">
            <div className="absolute top-0 right-0">
              <motion.div 
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-3 h-3 bg-emerald-400 rounded-full"
              />
            </div>
            
            <motion.div variants={slideInLeft} className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <Archive className="w-8 h-8" />
              </div>
              <div>
                <h4 className="font-bold text-xl">Archivage Légal</h4>
                <p className="text-slate-400">Conservation 10 ans garantie</p>
              </div>
            </motion.div>
            
            <motion.div variants={staggerContainer} className="space-y-3">
              {files.map((file, i) => (
                <motion.div 
                  key={i}
                  variants={slideInRight}
                  whileHover={{ x: 5 }}
                  className="bg-white/10 backdrop-blur rounded-xl p-4 flex items-center justify-between cursor-pointer hover:bg-white/15 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-400" />
                    <div>
                      <span className="font-medium">{file.name}</span>
                      <p className="text-xs text-slate-400">{file.date}</p>
                    </div>
                  </div>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                  >
                    <CheckCircle className="w-6 h-6 text-emerald-400" />
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>
            
            <motion.div 
              variants={fadeInUp}
              className="mt-6 flex items-center gap-2 text-sm text-slate-300"
            >
              <Lock className="w-4 h-4" />
              <span>Hash SHA-256 d'intégrité par document</span>
            </motion.div>
          </div>
        </motion.div>
        
        <motion.div 
          variants={scaleIn}
          className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-center"
        >
          <p className="text-amber-800 font-medium flex items-center justify-center gap-2">
            <FileCheck className="w-5 h-5" />
            Conforme Article L123-22 du Code de commerce
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

const SlideFEC = () => {
  const [downloading, setDownloading] = useState(false);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDownloading(true);
      setTimeout(() => setDownloading(false), 2000);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const columns = ["JournalCode", "EcritureNum", "CompteNum", "Debit", "Credit"];

  return (
    <motion.div 
      className="py-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <div className="max-w-lg mx-auto">
        <motion.div 
          variants={scaleIn}
          className="bg-white border-2 border-slate-200 rounded-2xl overflow-hidden shadow-xl"
        >
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database className="w-6 h-6 text-cyan-400" />
              <span className="font-bold text-white">Export comptable</span>
            </div>
            <motion.span 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xs bg-emerald-500 text-white px-3 py-1 rounded-full font-semibold"
            >
              Conforme LPF
            </motion.span>
          </div>
          
          <div className="p-6">
            <motion.div variants={fadeInUp} className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900">Fichier des Écritures Comptables</h4>
                <p className="text-slate-500 text-sm">Article A.47 A-1 du LPF</p>
              </div>
            </motion.div>
            
            <motion.div 
              variants={staggerContainer}
              className="bg-slate-900 rounded-xl p-4 mb-5 overflow-hidden"
            >
              <div className="flex flex-wrap gap-2">
                {columns.map((col, i) => (
                  <motion.span
                    key={i}
                    variants={scaleIn}
                    className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded text-xs font-mono"
                  >
                    {col}
                  </motion.span>
                ))}
                <span className="text-slate-500 text-xs font-mono">... +13</span>
              </div>
            </motion.div>
            
            <motion.div 
              variants={fadeInUp}
              className="flex items-center justify-between text-sm text-slate-600 mb-5 bg-slate-50 rounded-lg p-3"
            >
              <span className="font-medium">18 colonnes obligatoires</span>
              <CheckCircle className="w-5 h-5 text-emerald-500" />
            </motion.div>
            
            <motion.button
              variants={scaleIn}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl p-4 flex items-center justify-center gap-3 font-semibold shadow-lg shadow-blue-500/20 ${downloading ? 'opacity-80' : ''}`}
            >
              {downloading ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <RefreshCw className="w-5 h-5" />
                  </motion.div>
                  <span>Génération en cours...</span>
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  <span>Télécharger FEC</span>
                </>
              )}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const SlideConclusion = () => (
  <motion.div 
    className="text-center py-8"
    initial="hidden"
    animate="visible"
    variants={staggerContainer}
  >
    <motion.div 
      variants={scaleIn}
      className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-xl shadow-emerald-500/30"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.3, type: "spring" }}
      >
        <CheckCircle className="w-12 h-12 text-white" />
      </motion.div>
    </motion.div>
    
    <motion.h3 variants={fadeInUp} className="text-3xl font-bold text-slate-900 mb-4">
      Vous êtes prêt !
    </motion.h3>
    
    <motion.p variants={fadeInUp} className="text-slate-600 max-w-md mx-auto mb-10 text-lg">
      SmartFacture AI simplifie votre facturation tout en garantissant 
      conformité totale et sécurité maximale.
    </motion.p>
    
    <motion.div 
      variants={staggerContainer}
      className="flex flex-col sm:flex-row gap-5 justify-center"
    >
      {[
        { icon: Shield, label: "Sécurité", desc: "Chiffrement E2E", color: "blue" },
        { icon: CheckCircle, label: "Conformité", desc: "Législation FR", color: "emerald" },
        { icon: Sparkles, label: "Simplicité", desc: "Interface intuitive", color: "amber" },
      ].map((item, i) => (
        <motion.div
          key={i}
          variants={scaleIn}
          whileHover={{ y: -5 }}
          className={`bg-${item.color}-50 border border-${item.color}-200 rounded-2xl px-6 py-5 text-center min-w-[140px]`}
        >
          <item.icon className={`w-10 h-10 text-${item.color}-600 mx-auto mb-3`} />
          <span className="font-bold text-slate-900 block">{item.label}</span>
          <span className="text-sm text-slate-500">{item.desc}</span>
        </motion.div>
      ))}
    </motion.div>
  </motion.div>
);

// ============================================
// SLIDE COMPONENTS - Tutoriel Complet
// ============================================

const SlideOverview = () => (
  <motion.div 
    className="py-6"
    initial="hidden"
    animate="visible"
    variants={staggerContainer}
  >
    <motion.div 
      variants={scaleIn}
      className="bg-slate-900 rounded-2xl p-4 max-w-2xl mx-auto shadow-2xl"
    >
      <div className="bg-slate-800 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-3 h-3 bg-red-500 rounded-full"/>
          <div className="w-3 h-3 bg-yellow-500 rounded-full"/>
          <div className="w-3 h-3 bg-green-500 rounded-full"/>
          <span className="text-slate-400 text-xs ml-2">Dashboard SmartFacture AI</span>
        </div>
        
        <motion.div 
          variants={staggerContainer}
          className="grid grid-cols-3 gap-3 mb-4"
        >
          {[
            { label: "Chiffre d'affaires", value: "42 850 €", color: "from-blue-600 to-blue-500" },
            { label: "Factures payées", value: "38", color: "from-emerald-600 to-emerald-500" },
            { label: "En attente", value: "5", color: "from-amber-600 to-amber-500" },
          ].map((stat, i) => (
            <motion.div
              key={i}
              variants={scaleIn}
              className={`bg-gradient-to-br ${stat.color} rounded-xl p-4`}
            >
              <p className="text-white/70 text-xs mb-1">{stat.label}</p>
              <p className="text-white text-2xl font-bold">{stat.value}</p>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.div variants={fadeInUp} className="bg-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between text-sm text-slate-300 mb-3">
            <span className="font-medium">Dernières factures</span>
            <span className="text-blue-400 text-xs cursor-pointer hover:underline">Voir tout →</span>
          </div>
          <div className="space-y-2">
            {[
              { num: "INV-00038", client: "Cabinet Dupont", status: "Payée", color: "emerald" },
              { num: "INV-00037", client: "SARL Martin", status: "Envoyée", color: "blue" },
              { num: "INV-00036", client: "Auto-école Pro", status: "En retard", color: "red" },
            ].map((inv, i) => (
              <motion.div
                key={i}
                variants={slideInRight}
                className="bg-slate-600/50 rounded-lg p-3 flex items-center justify-between text-sm"
              >
                <div>
                  <span className="text-slate-200 font-medium">{inv.num}</span>
                  <span className="text-slate-400 ml-2">· {inv.client}</span>
                </div>
                <span className={`text-${inv.color}-400 text-xs font-medium`}>{inv.status}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  </motion.div>
);

const SlideAuditTrail = () => {
  const entries = [
    { action: "Facture créée", hash: "a7f2...3b91", time: "10:23" },
    { action: "Montant modifié", hash: "c4e8...9d12", time: "10:25" },
    { action: "Statut: Envoyée", hash: "f1b3...7e45", time: "10:28" },
  ];

  return (
    <motion.div 
      className="py-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <div className="max-w-lg mx-auto">
        <motion.div 
          variants={scaleIn}
          className="bg-gradient-to-br from-purple-900 via-indigo-900 to-purple-900 rounded-2xl p-6 text-white shadow-xl"
        >
          <motion.div variants={slideInLeft} className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur">
              <Shield className="w-7 h-7 text-purple-300" />
            </div>
            <div>
              <h4 className="font-bold text-xl">Audit Trail</h4>
              <p className="text-purple-300">Traçabilité type blockchain</p>
            </div>
          </motion.div>
          
          <motion.div variants={staggerContainer} className="space-y-3">
            {entries.map((item, i) => (
              <motion.div key={i} variants={fadeInUp} className="relative">
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-purple-500/30 rounded-full flex items-center justify-center font-mono text-sm font-bold">
                      #{i + 1}
                    </div>
                    <div>
                      <span className="font-medium">{item.action}</span>
                      <p className="text-xs text-purple-300">{item.time}</p>
                    </div>
                  </div>
                  <code className="text-xs text-purple-200 bg-black/20 px-3 py-1.5 rounded-lg font-mono">
                    {item.hash}
                  </code>
                </div>
                {i < entries.length - 1 && (
                  <div className="absolute left-7 top-full h-3 w-0.5 bg-purple-500/50"/>
                )}
              </motion.div>
            ))}
          </motion.div>
          
          <motion.div 
            variants={scaleIn}
            className="mt-6 bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-4 flex items-center gap-3"
          >
            <CheckCircle className="w-6 h-6 text-emerald-400 flex-shrink-0" />
            <span className="text-emerald-300 font-medium">Chaîne d'intégrité vérifiée</span>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const Slide2FA = () => {
  const [codeVisible, setCodeVisible] = useState([false, false, false, false, false, false]);
  const code = ['4', '8', '2', '9', '1', '7'];
  
  useEffect(() => {
    code.forEach((_, i) => {
      setTimeout(() => {
        setCodeVisible(prev => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, 300 + i * 150);
    });
  }, []);

  return (
    <motion.div 
      className="py-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <div className="max-w-sm mx-auto">
        <motion.div 
          variants={scaleIn}
          className="bg-white border-2 border-slate-200 rounded-2xl p-8 shadow-2xl"
        >
          <motion.div variants={fadeInUp} className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <Key className="w-10 h-10 text-amber-600" />
            </div>
            <h4 className="font-bold text-xl text-slate-900">Vérification 2FA</h4>
            <p className="text-slate-500 mt-1">Code envoyé par email</p>
          </motion.div>
          
          <motion.div 
            variants={staggerContainer}
            className="flex justify-center gap-2 mb-8"
          >
            {code.map((digit, i) => (
              <motion.div
                key={i}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ 
                  scale: codeVisible[i] ? 1 : 0, 
                  opacity: codeVisible[i] ? 1 : 0 
                }}
                transition={{ type: "spring", stiffness: 300 }}
                className="w-12 h-14 bg-slate-100 rounded-xl flex items-center justify-center text-2xl font-bold text-slate-900 border-2 border-slate-200"
              >
                {codeVisible[i] ? digit : ''}
              </motion.div>
            ))}
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5 }}
            className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3"
          >
            <CheckCircle className="w-6 h-6 text-emerald-600" />
            <span className="text-emerald-700 font-medium">Code vérifié avec succès</span>
          </motion.div>
        </motion.div>
        
        <motion.p 
          variants={fadeInUp}
          className="text-center text-slate-500 text-sm mt-4"
        >
          Obligatoire pour les administrateurs
        </motion.p>
      </div>
    </motion.div>
  );
};

const SlideRelances = () => {
  const relances = [
    { day: "J+7", status: "Rappel amical", color: "blue" },
    { day: "J+14", status: "Relance ferme", color: "amber" },
    { day: "J+21", status: "Mise en demeure", color: "red" },
  ];

  return (
    <motion.div 
      className="py-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <div className="max-w-lg mx-auto">
        <motion.div variants={scaleIn} className="bg-slate-50 rounded-2xl p-6">
          <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Send className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h4 className="font-bold text-slate-900">Relances Automatiques</h4>
              <p className="text-slate-500 text-sm">Emails personnalisables</p>
            </div>
          </motion.div>
          
          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200"/>
            
            <motion.div variants={staggerContainer} className="space-y-4">
              {relances.map((item, i) => (
                <motion.div
                  key={i}
                  variants={slideInRight}
                  className="relative flex items-center gap-4"
                >
                  <div className={`w-12 h-12 rounded-full bg-${item.color}-100 flex items-center justify-center z-10 border-4 border-white shadow`}>
                    <Mail className={`w-5 h-5 text-${item.color}-600`} />
                  </div>
                  <div className="flex-1 bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900">{item.day}</span>
                      <span className={`text-xs px-3 py-1 rounded-full bg-${item.color}-100 text-${item.color}-700 font-medium`}>
                        {item.status}
                      </span>
                    </div>
                    <p className="text-slate-500 text-sm mt-1">Email envoyé automatiquement</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const SlideRGPD = () => {
  const data = [
    { label: "Factures", count: "127" },
    { label: "Clients", count: "34" },
    { label: "Paiements", count: "89" },
    { label: "Journal d'audit", count: "412" },
  ];

  return (
    <motion.div 
      className="py-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <div className="max-w-lg mx-auto">
        <motion.div 
          variants={scaleIn}
          className="bg-gradient-to-br from-cyan-500 via-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-xl"
        >
          <motion.div variants={slideInLeft} className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur">
              <Users className="w-7 h-7" />
            </div>
            <div>
              <h4 className="font-bold text-xl">Vos Données</h4>
              <p className="text-cyan-100">RGPD Article 20</p>
            </div>
          </motion.div>
          
          <motion.div variants={staggerContainer} className="space-y-3 mb-6">
            {data.map((item, i) => (
              <motion.div
                key={i}
                variants={slideInRight}
                className="bg-white/10 backdrop-blur rounded-xl p-4 flex items-center justify-between"
              >
                <span className="font-medium">{item.label}</span>
                <span className="font-bold text-lg">{item.count}</span>
              </motion.div>
            ))}
          </motion.div>
          
          <motion.button
            variants={scaleIn}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-white text-blue-600 rounded-xl p-4 font-bold flex items-center justify-center gap-3 shadow-lg"
          >
            <Download className="w-5 h-5" />
            Exporter mes données (JSON)
          </motion.button>
        </motion.div>
        
        <motion.p 
          variants={fadeInUp}
          className="text-center text-slate-500 text-sm mt-4"
        >
          Droit à la portabilité garanti
        </motion.p>
      </div>
    </motion.div>
  );
};

// ============================================
// TUTORIAL DATA
// ============================================

const onboardingSlides = [
  { id: 1, title: "Introduction", component: SlideIntro },
  { id: 2, title: "Créer une facture", component: SlideCreation },
  { id: 3, title: "Archivage automatique", component: SlideArchivage },
  { id: 4, title: "Export comptable", component: SlideFEC },
  { id: 5, title: "C'est terminé !", component: SlideConclusion },
];

const fullTutorialSlides = [
  { id: 1, title: "Vue d'ensemble", component: SlideOverview },
  { id: 2, title: "Audit Trail Sécurisé", component: SlideAuditTrail },
  { id: 3, title: "Authentification 2FA", component: Slide2FA },
  { id: 4, title: "Relances Automatiques", component: SlideRelances },
  { id: 5, title: "Export RGPD", component: SlideRGPD },
];

// ============================================
// INTERACTIVE TUTORIAL COMPONENT
// ============================================

const InteractiveTutorial = ({ slides, title, duration }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    if (isPlaying) {
      const timer = setInterval(() => {
        setDirection(1);
        setCurrentSlide(prev => (prev + 1) % slides.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [isPlaying, slides.length]);

  const goToSlide = (index) => {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
    setIsPlaying(false);
  };

  const nextSlide = () => {
    setDirection(1);
    setCurrentSlide(prev => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setDirection(-1);
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  };

  const CurrentComponent = slides[currentSlide].component;

  return (
    <Card className="overflow-hidden border-slate-200 shadow-xl bg-white" data-testid="tutorial-card">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-6 py-4 flex items-center justify-between">
        <div>
          <h3 className="text-white font-bold text-lg">{title}</h3>
          <p className="text-slate-400 text-sm">{duration}</p>
        </div>
        <div className="flex items-center gap-3">
          <motion.button 
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsPlaying(!isPlaying)}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-lg ${
              isPlaying 
                ? 'bg-amber-500 shadow-amber-500/30' 
                : 'bg-blue-500 shadow-blue-500/30'
            }`}
            data-testid="play-pause-btn"
          >
            {isPlaying ? (
              <Pause className="w-5 h-5 text-white" />
            ) : (
              <Play className="w-5 h-5 text-white ml-0.5" />
            )}
          </motion.button>
        </div>
      </div>
      
      {/* Slide Content */}
      <div className="bg-gradient-to-b from-slate-50 to-white min-h-[450px] relative overflow-hidden">
        <div className="absolute top-4 left-0 right-0 text-center z-10">
          <motion.span 
            key={currentSlide}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 bg-white rounded-full text-sm font-semibold text-slate-700 shadow-sm border border-slate-200"
          >
            {slides[currentSlide].title}
          </motion.span>
        </div>
        
        <div className="pt-14 px-6 pb-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, x: direction * 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -direction * 50 }}
              transition={{ duration: 0.3 }}
            >
              <CurrentComponent />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      
      {/* Controls */}
      <div className="bg-white px-6 py-4 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <motion.button 
            whileHover={{ x: -3 }}
            onClick={prevSlide}
            className="flex items-center gap-1 text-slate-600 hover:text-slate-900 transition-colors font-medium"
            data-testid="prev-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">Précédent</span>
          </motion.button>
          
          {/* Progress dots */}
          <div className="flex gap-2">
            {slides.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => goToSlide(index)}
                whileHover={{ scale: 1.2 }}
                className={`h-2.5 rounded-full transition-all ${
                  index === currentSlide 
                    ? 'bg-blue-600 w-8' 
                    : 'bg-slate-300 hover:bg-slate-400 w-2.5'
                }`}
                data-testid={`slide-dot-${index}`}
              />
            ))}
          </div>
          
          <motion.button 
            whileHover={{ x: 3 }}
            onClick={nextSlide}
            className="flex items-center gap-1 text-slate-600 hover:text-slate-900 transition-colors font-medium"
            data-testid="next-btn"
          >
            <span className="text-sm">Suivant</span>
            <ChevronRight className="w-5 h-5" />
          </motion.button>
        </div>
      </div>
    </Card>
  );
};

// ============================================
// MAIN PAGE COMPONENT
// ============================================

const TutorialPage = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="tutorial-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">SmartFacture AI</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" data-testid="login-btn">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/20" data-testid="register-btn">
                Essai gratuit
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <motion.section 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20 relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}/>
        </div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            Tutoriels Interactifs
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-slate-300 max-w-2xl mx-auto"
          >
            Maîtrisez SmartFacture AI en quelques minutes avec nos guides animés.
          </motion.p>
        </div>
      </motion.section>

      {/* Tutorials Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 space-y-20">
          
          {/* Tutorial 1 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                <span className="text-white font-bold text-lg">1</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Prise en main rapide</h2>
                <p className="text-slate-600">5 étapes essentielles • ~2 min</p>
              </div>
            </div>
            <InteractiveTutorial 
              slides={onboardingSlides} 
              title="Onboarding SmartFacture AI"
              duration="5 étapes • Lecture auto disponible"
            />
          </motion.div>
          
          {/* Tutorial 2 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <span className="text-white font-bold text-lg">2</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Fonctionnalités avancées</h2>
                <p className="text-slate-600">Sécurité & Conformité • ~4 min</p>
              </div>
            </div>
            <InteractiveTutorial 
              slides={fullTutorialSlides} 
              title="Tutoriel Complet"
              duration="5 étapes • Sécurité & Export"
            />
          </motion.div>
          
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Conformité & Sécurité
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              SmartFacture AI respecte l'ensemble des exigences légales françaises.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {[
              { icon: FileText, title: "Export comptable", desc: "Format CSV standard", gradient: "from-blue-500 to-blue-600" },
              { icon: Archive, title: "Archivage 10 ans", desc: "Article L123-22", gradient: "from-emerald-500 to-emerald-600" },
              { icon: Shield, title: "Audit Trail", desc: "Hash SHA-256 chaîné", gradient: "from-purple-500 to-purple-600" },
              { icon: Key, title: "2FA", desc: "OTP par email", gradient: "from-amber-500 to-amber-600" },
              { icon: Users, title: "RGPD", desc: "Export & suppression", gradient: "from-cyan-500 to-cyan-600" },
              { icon: Clock, title: "Relances auto", desc: "J+7, J+14, J+21", gradient: "from-rose-500 to-rose-600" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <Card className="border-slate-200 hover:border-blue-300 transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mb-4 shadow-lg`}>
                      <item.icon className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="font-bold text-slate-900 text-lg mb-1">{item.title}</h3>
                    <p className="text-slate-500">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '30px 30px'
          }}/>
        </div>
        <div className="max-w-3xl mx-auto px-4 text-center relative z-10">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-white mb-4"
          >
            Prêt à commencer ?
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-blue-100 mb-8 text-lg"
          >
            Créez votre compte gratuitement et testez SmartFacture AI.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-blue-50 h-14 px-10 text-lg font-bold shadow-xl"
                data-testid="cta-register-btn"
              >
                Créer mon compte gratuit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-10">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-white">SmartFacture AI</span>
          </div>
          <p className="text-sm">© 2026 SmartFacture AI - Solution de facturation conforme</p>
        </div>
      </footer>
    </div>
  );
};

export default TutorialPage;
